/**
 * Ladies Hub — Custom fashion/beauty SVG icons
 *
 * Each icon follows the Lucide visual spec:
 *   viewBox 0 0 24 24  ·  stroke currentColor  ·  strokeWidth 2
 *   strokeLinecap round  ·  strokeLinejoin round  ·  fill none
 *
 * Usage:  <DressIcon size={18} strokeWidth={1.8} />
 *         Same API as any Lucide icon component.
 */

import React from 'react';

const base = {
  xmlns: 'http://www.w3.org/2000/svg',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function icon(paths) {
  return React.forwardRef(({ size = 24, strokeWidth = 2, ...rest }, ref) => (
    <svg
      ref={ref}
      {...base}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      strokeWidth={strokeWidth}
      {...rest}
    >
      {paths}
    </svg>
  ));
}

/* ── FASHION ─────────────────────────────────────────── */

/** Dress — A-line dress silhouette */
export const DressIcon = icon(<>
  <path d="M12 2v4" />
  <path d="M9 2h6" />
  <path d="M10 6l-4 16h12l-4-16" />
  <path d="M10 6h4" />
</>);

/** Traditional dress — garment with waist sash (Sudanese thobe) */
export const TraditionalDressIcon = icon(<>
  <path d="M12 2v3" />
  <path d="M9 2h6" />
  <path d="M10 5l-4 17h12l-4-17" />
  <path d="M7.5 13h9" />
  <path d="M8 11h8" />
</>);

/** Long robe — flowing abaya/kaftan silhouette */
export const RobeIcon = icon(<>
  <path d="M12 2v3" />
  <path d="M10 2h4" />
  <path d="M10 5c-1 2-3 4-4 6v9h12v-9c-1-2-3-4-4-6" />
  <path d="M6 20h12" />
</>);

/** Gown — elegant dress with flare (occasion/event wear) */
export const GownIcon = icon(<>
  <path d="M12 2v4" />
  <path d="M10 2h4" />
  <path d="M10 6l-1 4c-1.5 3-4 6-4 12h14c0-6-2.5-9-4-12l-1-4" />
  <path d="M9 10h6" />
</>);

/** Headscarf — draped hijab/scarf */
export const HeadscarfIcon = icon(<>
  <circle cx="12" cy="9" r="4" />
  <path d="M7 7c-1 1-2 3-2 5s1 5 4 7" />
  <path d="M17 7c1 1 2 3 2 5s-1 4-3 6l-3 2" />
  <path d="M8 13c0 2 1 4 3 6" />
</>);

/** High heel shoe */
export const HighHeelIcon = icon(<>
  <path d="M3 18h18" />
  <path d="M4 18c0-4 2-7 5-9l2-1" />
  <path d="M11 8c2-1 5-1 7 0" />
  <path d="M18 8v7c0 1.5.5 2.5 2 3" />
  <path d="M4 18l1-5" />
</>);

/** Handbag with handle */
export const HandbagIcon = icon(<>
  <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  <rect x="4" y="10" width="16" height="10" rx="2" />
  <path d="M12 14v2" />
</>);

/** Ring with gemstone */
export const RingIcon = icon(<>
  <circle cx="12" cy="14" r="6" />
  <path d="M9 8l3-4 3 4" />
  <path d="M9 8h6" />
</>);

/* ── BEAUTY ──────────────────────────────────────────── */

/** Lipstick */
export const LipstickIcon = icon(<>
  <rect x="8" y="10" width="8" height="10" rx="1" />
  <path d="M10 10V7h4v3" />
  <path d="M10 7l2-5 2 5" />
  <line x1="8" y1="14" x2="16" y2="14" />
</>);

/** Hair comb */
export const HairCombIcon = icon(<>
  <rect x="4" y="3" width="16" height="5" rx="2" />
  <line x1="7" y1="8" x2="7" y2="21" />
  <line x1="10" y1="8" x2="10" y2="21" />
  <line x1="13" y1="8" x2="13" y2="21" />
  <line x1="16" y1="8" x2="16" y2="21" />
</>);

/** Perfume bottle with spray */
export const PerfumeIcon = icon(<>
  <rect x="7" y="10" width="10" height="11" rx="2" />
  <path d="M10 10V8h4v2" />
  <line x1="12" y1="5" x2="12" y2="8" />
  <line x1="12" y1="2" x2="12" y2="3" />
  <path d="M10 4l-2-1" />
  <path d="M14 4l2-1" />
</>);

/** Hand with henna pattern */
export const HennaHandIcon = icon(<>
  <path d="M18 11V6a1 1 0 0 0-2 0" />
  <path d="M16 8V4a1 1 0 0 0-2 0" />
  <path d="M14 6V3a1 1 0 0 0-2 0v7" />
  <path d="M10 7V5a1 1 0 0 0-2 0v6" />
  <path d="M8 11c-2 1-3 3-3 5v1a7 7 0 0 0 14 0v-1c0-1-1-2-1-2" />
  <circle cx="12" cy="17" r="1" />
</>);

/* ── JEWELLERY ───────────────────────────────────────── */

/** Necklace — chain with pendant */
export const NecklaceIcon = icon(<>
  <path d="M6 4c0 6 3 9 6 10 3-1 6-4 6-10" />
  <circle cx="12" cy="16" r="2" />
  <line x1="12" y1="18" x2="12" y2="21" />
  <path d="M10 21h4" />
</>);

/** Earring — drop earring */
export const EarringIcon = icon(<>
  <path d="M12 2v3" />
  <circle cx="12" cy="4" r="2" />
  <path d="M12 6v2" />
  <path d="M8 10c0 3 2 6 4 8 2-2 4-5 4-8a4 4 0 0 0-8 0z" />
  <circle cx="12" cy="12" r="1.5" />
</>);

/** Bracelet — circular band */
export const BraceletIcon = icon(<>
  <ellipse cx="12" cy="12" rx="8" ry="5" />
  <path d="M4 12c0-1.5 1-3 3-4" />
  <path d="M20 12c0 1.5-1 3-3 4" />
  <circle cx="12" cy="7" r="1" />
</>);

/* ── HANDMADE ────────────────────────────────────────── */

/** Yarn ball with thread */
export const YarnIcon = icon(<>
  <circle cx="12" cy="12" r="8" />
  <path d="M7 7c4 1 7 4 8 8" />
  <path d="M5 12c3-1 6 0 8 3" />
  <path d="M12 5c-1 3 0 6 3 8" />
  <path d="M19 15l2 3" />
</>);

/** Needle with thread */
export const NeedleThreadIcon = icon(<>
  <line x1="17" y1="3" x2="7" y2="21" />
  <path d="M17 3l1 1" />
  <path d="M15 7c-3-1-5 0-6 3" />
  <path d="M9 10c-2 1-3 4-1 6" />
</>);

/** Fabric/textile swatch */
export const FabricIcon = icon(<>
  <rect x="3" y="3" width="18" height="18" rx="2" />
  <path d="M3 9h18" />
  <path d="M3 15h18" />
  <path d="M9 3v18" />
  <path d="M15 3v18" />
</>);

/** Candle with flame */
export const CandleIcon = icon(<>
  <rect x="9" y="10" width="6" height="12" rx="1" />
  <path d="M12 10V7" />
  <path d="M10 5c0-2 2-3 2-3s2 1 2 3-1 2-2 2-2 0-2-2z" />
</>);

/** Soap bar with bubbles */
export const SoapIcon = icon(<>
  <rect x="3" y="12" width="14" height="8" rx="2" />
  <path d="M5 12v-1a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1" />
  <circle cx="18" cy="6" r="2" />
  <circle cx="20" cy="10" r="1.5" />
  <circle cx="16" cy="4" r="1" />
</>);

/** Vase — pottery / ceramics */
export const VaseIcon = icon(<>
  <path d="M9 3h6" />
  <path d="M10 3v2c0 1-1 2-2 3v2c0 2 1 4 2 6v2H8v2h8v-2h-2v-2c1-2 2-4 2-6v-2c-1-1-2-2-2-3V3" />
  <path d="M8 20h8" />
</>);
